import { GetCurrentProjectData, GetProjectData, applySelectedProjectDetail } from '../redux/projectDataSlice';
import { setActiveWorkspaceId } from '../redux/usersSlice';
import store from '../redux/store';

export function toPulseAppPath(deepLink) {
    if (!deepLink) return null;
    if (deepLink.startsWith('/')) return deepLink;
    try {
        const url = new URL(deepLink);
        return `${url.pathname}${url.search}`;
    } catch (e) {
        return null;
    }
}

/**
 * Map /projects/:id/... deep links to in-app routes (overview, tasks, irl, etc.).
 */
export function resolvePulseProjectDestination(appPath) {
    const normalized = toPulseAppPath(appPath);
    if (!normalized) return null;

    const [pathname, rawQuery = ''] = normalized.split('?');
    const match = pathname.match(/^\/projects\/([^/]+)(?:\/(.*))?$/);
    if (!match) return null;

    const projectId = match[1];
    const rest = match[2] || '';
    let destination = '/overview';

    if (rest.startsWith('tasks/')) {
        const taskId = rest.split('/')[1];
        destination = taskId ? `/tasks?taskId=${encodeURIComponent(taskId)}` : '/tasks';
    } else if (rest === 'dataroom/irl' || rest.startsWith('dataroom/irl/')) {
        const irlId = rest.startsWith('dataroom/irl/') ? rest.split('/')[2] : '';
        destination = irlId ? `/irl?irlId=${encodeURIComponent(irlId)}` : '/irl';
    } else if (rest === 'dataroom') {
        destination = '/dataroom';
    } else if (rest === 'raid') {
        destination = '/raid';
    } else if (rest === 'observations') {
        destination = '/observations';
    } else if (rest === 'action-log') {
        destination = '/action-log';
    } else if (rawQuery) {
        destination = `${destination}?${rawQuery}`;
    }

    return { projectId, destination };
}

/**
 * Ensure Redux selectedProject + workspace match localStorage project_id (Pulse email deep links).
 * Returns true when the target project is active in the store.
 */
export async function ensurePulseProjectActive(dispatch, projectId) {
    const targetId = projectId || localStorage.getItem('project_id');
    if (!targetId) {
        return false;
    }

    const current = store.getState().projectData?.selectedProject;
    if (current && String(current.id) === String(targetId)) {
        return true;
    }

    localStorage.setItem('project_id', targetId);

    let projectDetail = null;
    try {
        const detailRes = await dispatch(GetCurrentProjectData()).unwrap();
        projectDetail = detailRes?.data;
    } catch {
        return false;
    }

    if (!projectDetail || String(projectDetail.id) !== String(targetId)) {
        return false;
    }

    const workspaceId = projectDetail.workspace ?? projectDetail.workspace_id;
    if (workspaceId) {
        dispatch(setActiveWorkspaceId(workspaceId));
        localStorage.setItem('active_workspace_id', String(workspaceId));
    }

    // Do not await — project list refresh can hang in background tabs and blocks navigation.
    dispatch(GetProjectData());

    dispatch(applySelectedProjectDetail(projectDetail));

    const loaded = store.getState().projectData?.selectedProject;
    return Boolean(loaded && String(loaded.id) === String(targetId));
}

/**
 * In-app navigation from Workspace Pulse — avoids hard reload via PulseDeepLinkRedirect.
 */
export async function navigatePulseProjectLink(dispatch, navigate, target) {
    const appPath = toPulseAppPath(target) || target;
    if (!appPath) return false;

    const resolved = resolvePulseProjectDestination(appPath);
    if (!resolved) {
        navigate(appPath);
        return true;
    }

    const ok = await ensurePulseProjectActive(dispatch, resolved.projectId);
    if (!ok) return false;

    navigate(resolved.destination);
    return true;
}
